 
import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import StepShell from './StepShell';
import StepTitleWithInfo from './StepTitleWithInfo';
import type { FlowCtx } from './types';
import { Info, Power } from 'lucide-react';

interface Props {
  ctx: FlowCtx;
}

type SubStep = 'main' | 'replug' | 'powerSwitch';

const Step1TurnOnDevice: React.FC<Props> = ({ ctx }) => {
 
  const [subStep, setSubStep] = useState<SubStep>('main');
  const [replugAnim, setReplugAnim] = useState<Record<string, unknown> | null>(null);

  /* Get System status */
  useEffect(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'GET_SYSTEM_STATUS' }),
    );
      window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'CHECK_LOCATION' }),
    );
   
  }, [])


  useEffect(() => {
    if (!replugAnim) {
      fetch(`${process.env.PUBLIC_URL}/DeviceSetup/re-plugging.json`)
        .then((res) => res.json())
        .then(setReplugAnim)
        .catch(() => {});
    }
  }, [subStep, replugAnim]);

  // Advance the flow to Step 2. Step 2 surfaces the location check itself if
  // the permission still isn't granted.
  const goToStep2 = () => {
    setTimeout(() => {
      ctx.goTo('set');
    }, 1000);
  };

  /* ── Sub-step: Let's try re-plugging your device ── */
  if (subStep === 'replug') {
    return (
      <StepShell
        stepNumber={1}
        onBack={() => setSubStep('main')}
        secondary={{ label: 'Light is not Green', onClick: () => setSubStep('powerSwitch') }}
        primary={{ label: 'Light is Green', onClick: goToStep2 }}
      >
        <div className="dcf-content--center">
          <StepTitleWithInfo>
            <h2 className="dcf-step-title">
              Let&apos;s try re-plugging your device
            </h2>
          </StepTitleWithInfo>
          <div className="dcf-step-bar" />

          <div className="dcf-image-source">
            {replugAnim ? (
              <Lottie
                animationData={replugAnim}
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
                  type="image/avif"
                />
                <img
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
                  alt="Unplug and replug your SmartEco device"
                  className="dcf-illustration"
                />
              </picture>
            )}
          </div>

          <div className="dcf-image-description--container">
            <Power size={14} color="var(--color-sub-title-text)" />
            <span className="dcf-image-description">
              Indicator light-is OFF
            </span>
          </div>

          <p className="dcf-step-subtitle">
            Unplug your device, wait a few seconds, then plug it back in firmly.
            Watch for the indicator light to turn solid green.
          </p>
        </div>
      </StepShell>
    );
  }

  /* ── Sub-step: Let's try the power switch ── */
  if (subStep === 'powerSwitch') {
    return (
      <StepShell
        stepNumber={1}
        onBack={() => setSubStep('replug')}
        secondary={{ label: 'Try Again', onClick: () => setSubStep('replug') }}
        primary={{ label: 'Done', onClick: goToStep2 }}
      >
        <div className="dcf-content--center">
          <StepTitleWithInfo>
            <h2 className="dcf-step-title">
              Let&apos;s try the power switch
            </h2>
          </StepTitleWithInfo>
          <div className="dcf-step-bar" />

          <div className="dcf-image-source">
            <img
              src={`${process.env.PUBLIC_URL}/DeviceSetup/power-switch.webp`}
              alt="Toggle the power switch on your SmartEco device"
              className="dcf-illustration"
            />
          </div>

          <div className="dcf-image-description--container">
            <Power size={14} color="var(--color-sub-title-text)" />
            <span className="dcf-image-description">
              Indicator light-is OFF
            </span>
          </div>

          <p className="dcf-step-subtitle">
            Hold the power switch on the back of your device. Turn it OFF, wait
            a moment, then turn it back ON. Watch for the indicator light to
            turn solid green.
          </p>
        </div>
      </StepShell>
    );
  }

  /* ── Main step 1 ── */
  return (
    <StepShell
      stepNumber={1}
      onBack={ctx.openExitModal}
      secondary={{ label: 'Light is not Green', onClick: () => setSubStep('replug') }}
      primary={{
        label: 'Light is Green',
        onClick: goToStep2,
      }}
    >
      <div className="dcf-content--center">
        <StepTitleWithInfo>
          <h2 className="dcf-step-title">Step 1 of 4: Let&apos;s get your Smart Eco
            device started</h2>
        </StepTitleWithInfo> 
        <div className="dcf-step-bar" />
        <div className="dcf-image-source">
          <picture>
            <source
              srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
              type="image/avif"
            />
            <img
              srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
              alt="SmartEco IAQ device"
              className="dcf-illustration"
            />
          </picture>

          <picture className="dcf-indicator">
            <source
              srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/step1-indicator.avif`}
              type="image/avif"
            />
            <img
              src={`${process.env.PUBLIC_URL}/DeviceSetup/step1-indicator.webp`}
              alt="SmartEco IAQ device"
              className="dcf-indicator-image"
            />
          </picture>
        </div>
        <div className="dcf-image-description--container">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="var(--color-text)" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.78425 2.97836C9.62022 3.14244 9.52806 3.36496 9.52806 3.59698C9.52806 3.829 9.62022 4.05152 9.78425 4.21561C10.3349 4.76628 10.7099 5.46787 10.8618 6.23167C11.0138 6.99547 10.9358 7.78717 10.6378 8.50665C10.3398 9.22613 9.83507 9.84108 9.18756 10.2737C8.54004 10.7064 7.77876 10.9373 7 10.9373C6.22124 10.9373 5.45997 10.7064 4.81245 10.2737C4.16493 9.84108 3.66025 9.22613 3.36223 8.50665C3.06421 7.78717 2.98623 6.99547 3.13816 6.23167C3.29008 5.46787 3.66509 4.76628 4.21575 4.21561C4.37514 4.05058 4.46334 3.82955 4.46134 3.60013C4.45935 3.37071 4.36733 3.15125 4.20509 2.98901C4.04286 2.82678 3.8234 2.73476 3.59398 2.73277C3.36456 2.73077 3.14353 2.81897 2.9785 2.97836C2.18304 3.77375 1.64131 4.78716 1.42182 5.89044C1.20233 6.99372 1.31493 8.13731 1.74539 9.1766C2.17585 10.2159 2.90483 11.1042 3.84015 11.7291C4.77546 12.3541 5.8751 12.6877 7 12.6877C8.1249 12.6877 9.22454 12.3541 10.1599 11.7291C11.0952 11.1042 11.8242 10.2159 12.2546 9.1766C12.6851 8.13731 12.7977 6.99372 12.5782 5.89044C12.3587 4.78716 11.817 3.77375 11.0215 2.97836C10.8574 2.81432 10.6349 2.72217 10.4029 2.72217C10.1709 2.72217 9.94834 2.81432 9.78425 2.97836Z" />
            <path d="M7 6.9125C7.23171 6.91135 7.4536 6.81879 7.61745 6.65495C7.78129 6.4911 7.87385 6.26921 7.875 6.0375V2.1875C7.875 1.95544 7.78281 1.73288 7.61872 1.56878C7.45462 1.40469 7.23206 1.3125 7 1.3125C6.76794 1.3125 6.54538 1.40469 6.38128 1.56878C6.21719 1.73288 6.125 1.95544 6.125 2.1875V6.0375C6.12615 6.26921 6.21871 6.4911 6.38255 6.65495C6.5464 6.81879 6.76829 6.91135 7 6.9125Z" />
          </svg>
          <span className="dcf-image-description">Indicator light-is ON</span>
        </div>
        <p className="dcf-step-subtitle">
          Plug in your device and watch the indicator light at the base. It
          should turn solid green within 5 seconds.
        </p>
        <div className="dcf-banner" style={{ marginTop: 4 }}>
          <Info size={20} color="#EEC94D" className="flex-shrink-0" />
          <div className="dcf-banner-description">
            <span>Waiting for solid green?</span>
            <span>
              Hold on until the light is steady. If it&apos;s still blinking after 5
              seconds, unplug your device and plug it back in.
            </span>
 
          </div>
        </div>
      </div>
    </StepShell>
  );
};

export default Step1TurnOnDevice;
